import { Box } from '@mui/material';
import { vec2, vec3 } from 'gl-matrix';
import React, { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { useViewportDragHandle } from '~/viewportGrid/adapters/engine/overlays/useViewportDragHandle';
import {
    couchTopWorldPoint,
    worldPointToCouchTopVoxelY,
} from '~/domain/couchtop/geometry';
import { placeCouchModelOnSlice } from '~/domain/couchtop/placeCouchModel';
import { Grid } from '~/engine/data/common/Grid';
import {
    DashedGhostPreviewRenderer,
    GhostPreviewPolygon,
} from '~/engine/view/DashedGhostPreviewRenderer';
import { viewShowsAcquiredPrimarySlice } from '~/engine/view/planeViewGeometry';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useCouchTopRow, useSetCouchTopRow } from '~/hooks/useCouchTopRow';
import { couchModelPreviewSelectionState } from '~/state/atoms/couchTopAtoms';
import { operationPanelState } from '~/state/atoms/toolsAtoms';
import { dicomColorToVec3 } from '~/utils/color';

/** Accent colour, matching the measurement tool's draft/in-progress
 *  accent — the couch line is likewise an editable, uncommitted overlay. */
const LINE_COLOR = '#4FC3F7';
const LINE_COLOR_DRAGGING = '#FFFFFF';

export interface CouchTopLineProps {
    view: PlaneView | null;
    /**
     * Bumped by the wrapper (`index.tsx`) on every plane-intersection
     * change (slice scroll), every zoom change and every change of which
     * image of a fused pair the view draws (`usePlaneRedrawTick`'s
     * default events). Row/selection/view can all stay the SAME object
     * across each of them, so this is the one dependency that actually
     * changes identity on every event the line and preview must redraw
     * for: an explicit, checkable trigger rather than an effect with no
     * array at all (which `react-hooks/exhaustive-deps` cannot verify).
     */
    renderTick: number;
}

/**
 * The view's current slice, and the couch top's patient-frame point on it.
 *
 * Voxel CORNERS sit at integers, CENTERS at N+0.5; the continuous `voxelZ`
 * fed to {@link couchTopWorldPoint} is this slice's CENTER, matching
 * `placeCouchModel`'s own `z + 0.5` and the continuous Z a drag's
 * `clientToPatientCoordinates` point carries (the view snaps to voxel
 * centers).
 */
function resolveCurrentSlice(
    view: PlaneView,
    grid: Grid,
    couchTopVoxelY: number
): { sliceIndex: number; worldPoint: vec3 } {
    const sliceIndex = Math.floor(
        grid.toVoxelSpace(view.planeIntersectionPoint.coordinates)[2]
    );
    const worldPoint = couchTopWorldPoint(
        couchTopVoxelY,
        grid,
        grid.voxelToWorld,
        sliceIndex + 0.5
    );
    return { sliceIndex, worldPoint };
}

/**
 * Draggable couch-top line, drawn only on a view in the image's own slice
 * plane that draws the primary image ({@link viewShowsAcquiredPrimarySlice}),
 * following the SAME idiom as `Crosshairs.tsx`: an SVG with a transparent wide hit line plus a
 * thin visible line, percent-positioned; a crosshair drag held through
 * the viewer ToolManager `beginCrosshairDrag` for the gesture, which shows
 * `ns-resize` and suppresses the engine tools; `document` listeners scoped
 * by an `AbortController`, torn down on unmount.
 *
 * Rendered ONLY while the couch panel is open: the line is this tool's
 * editing affordance, and a closed tool must not leave a draggable
 * overlay capturing clicks over the CT while the user contours. The row
 * itself persists in Recoil, so reopening the panel restores the
 * placement.
 *
 * World<->screen conversion goes through `PlaneView.clientToPatientCoordinates`
 * / `canvasCoordsFromPatientCoords` exclusively — never hand-rolled
 * percentage arithmetic on `clientX`/`clientY` — because oblique series
 * exist and those two methods are the only place that geometry is
 * correctly handled.
 *
 * The depth->screen conversion ({@link couchTopWorldPoint}) needs an
 * EXPLICIT voxel Z, and this component always uses the CURRENT slice's,
 * resolved once per render through `resolveCurrentSlice`. Any other Z (a
 * stale one, or a hardcoded 0) would make a drag->redraw round-trip
 * non-idempotent on an oblique grid.
 */
export default function CouchTopLine({ view, renderTick }: CouchTopLineProps) {
    const setCouchTopRow = useSetCouchTopRow();
    const previewSelection = useRecoilValue(couchModelPreviewSelectionState);
    const panelState = useRecoilValue(operationPanelState);
    const { isDragging, startDrag } = useViewportDragHandle();
    const previewRendererRef = useRef<DashedGhostPreviewRenderer | null>(null);
    // Whether the renderer currently shows a preview. The overlay it draws
    // on is the view's SHARED 2D canvas, and clearing it wipes whatever
    // else is there — so it is cleared only when there is a preview of ours
    // to remove, never on every redraw of a view the tool is not open on.
    const hasPreviewRef = useRef(false);

    // The preview renderer owns a 2D canvas context — acquired and
    // released together, same rule as any GPU/engine resource. Held
    // across renders (the underlying canvas is the view's single shared
    // 2D overlay), rebuilt only when the VIEW changes.
    useEffect(() => {
        if (!view) {
            previewRendererRef.current = null;
            return;
        }
        let renderer: DashedGhostPreviewRenderer | null = null;
        try {
            renderer = new DashedGhostPreviewRenderer(view);
        } catch (err) {
            // The 2D canvas isn't attached yet (e.g. still mounting).
            // Non-fatal, but NOT retried either: only a view change
            // re-runs this effect, so the preview stays blank until the
            // view object itself is replaced. The line and readout stay
            // fully functional; only the ghost is affected.
            console.warn('CouchTopLine: preview renderer unavailable', err);
        }
        previewRendererRef.current = renderer;
        hasPreviewRef.current = false;
        return () => {
            renderer?.dispose();
            previewRendererRef.current = null;
            hasPreviewRef.current = false;
        };
    }, [view]);

    const isPanelOpen = panelState.type === 'couch';
    const image = view?.environment.image;

    const row = useCouchTopRow(image?.parent.seriesUid);

    const isActive =
        isPanelOpen && view !== null && viewShowsAcquiredPrimarySlice(view);

    // Resolved once, for the screen position here and the curve preview
    // below, so the two cannot disagree on the slice.
    const slice =
        isActive && view && image && row
            ? resolveCurrentSlice(view, image.grid, row.voxelY)
            : null;

    // Screen position (percent of the view's own pixel height, matching
    // Crosshairs' `horizontal` convention) — null when there is nothing to
    // draw. The couch-top point comes back whole from the domain helper,
    // never a scalar written into a fixed component: which patient axis
    // carries the depth follows the direction cosines, so a hardcoded world
    // Y leaves the line invariant in the depth on a decubitus series.
    let percentY: number | null = null;
    if (slice && view) {
        const canvasCoords = view.canvasCoordsFromPatientCoords(
            slice.worldPoint
        );
        const size = view.size;
        if (size[1] > 0) {
            percentY = (canvasCoords[1] / size[1]) * 100;
        }
    }

    // A primitive, so the effect below follows the slice rather than the
    // `slice` object, which is a fresh allocation on every render.
    const sliceIndex = slice?.sliceIndex ?? null;

    // Draw the uncommitted curve preview. `renderTick` is the dependency
    // that makes a slice scroll, a zoom or a fusion side change redraw
    // this: the wrapper bumps it on every `planeIntersectionPointChanged`,
    // `zoomScaleChanged` and `fusedImageDisplayChanged` event, when
    // `view`/`row`/`previewSelection` can all keep the SAME
    // identity (none is a Recoil write and none replaces `view`).
    // So without `renderTick` in the array a scroll would leave the ghost
    // stuck on the old slice and a zoom would leave it at the old scale.
    useEffect(() => {
        const renderer = previewRendererRef.current;
        if (!renderer) {
            return;
        }
        const clearPreview = () => {
            if (hasPreviewRef.current) {
                renderer.clear();
                hasPreviewRef.current = false;
            }
        };
        if (
            sliceIndex === null ||
            !view ||
            !image ||
            !row ||
            !previewSelection
        ) {
            clearPreview();
            return;
        }
        if (
            previewSelection.structureColors.length !==
            previewSelection.variant.components.length
        ) {
            // Defensive: the panel's state shape keeps these aligned (its
            // rows are stored WITH the variant they were seeded for), so
            // this is unreachable from the UI — kept as a cheap last line
            // of defence against a future writer of the atom, skipping the
            // frame rather than mis-colouring a component.
            clearPreview();
            return;
        }

        const grid = image.grid;

        try {
            // Only the CURRENT slice is placed: this effect re-runs per
            // drag mousemove and per name-field keystroke, and placing
            // every slice of a long CT just to draw one would put a
            // volume-sized allocation on the interaction path. The depth
            // is handed through directly — placement resolves it in voxel
            // space itself, the same maths Apply runs.
            const placed = placeCouchModelOnSlice(
                previewSelection.variant,
                row.voxelY,
                grid,
                sliceIndex
            );
            const polygons: GhostPreviewPolygon[] = placed.flatMap(
                ({ contours }, i) => {
                    const color = dicomColorToVec3(
                        previewSelection.structureColors[i]
                    );
                    return contours.map((contour) => ({
                        points: contour.points,
                        color,
                    }));
                }
            );
            renderer.setState({ polygons });
            hasPreviewRef.current = true;
        } catch (err) {
            // Non-axis-aligned grid (or any other placement failure):
            // Apply will refuse and surface this
            // loudly — the preview just shows nothing rather than a wrong
            // ghost. Logged, not swallowed: without it a genuine
            // placement bug would present only as "the ghost doesn't
            // draw", with nothing to point an engineer at the cause.
            console.warn('CouchTopLine: couch model preview failed', err);
            clearPreview();
        }
    }, [sliceIndex, view, image, row, previewSelection, renderTick]);

    const handleMouseDown = (
        event: React.MouseEvent<SVGElement, MouseEvent>
    ) => {
        event.preventDefault();
        if (!view || !image) {
            return;
        }

        const grid = image.grid;
        const seriesUid = image.parent.seriesUid;

        startDrag({
            cursor: 'ns-resize',
            onMove: (moveEvent) => {
                const world = view.clientToPatientCoordinates(
                    vec2.fromValues(moveEvent.clientX, moveEvent.clientY)
                );
                setCouchTopRow(seriesUid, {
                    voxelY: worldPointToCouchTopVoxelY(
                        world,
                        grid.voxelToWorld
                    ),
                });
            },
        });
    };

    if (percentY === null) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 6,
            }}
        >
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                }}
            >
                {/* Invisible wide hit area */}
                <line
                    x1="0%"
                    y1={`${percentY}%`}
                    x2="100%"
                    y2={`${percentY}%`}
                    stroke="transparent"
                    strokeWidth="10"
                    style={{ pointerEvents: 'auto', cursor: 'ns-resize' }}
                    onMouseDown={handleMouseDown}
                    data-testid="couch-top-line-hit"
                />
                {/* Visible line */}
                <line
                    x1="0%"
                    y1={`${percentY}%`}
                    x2="100%"
                    y2={`${percentY}%`}
                    stroke={isDragging ? LINE_COLOR_DRAGGING : LINE_COLOR}
                    strokeWidth="1.5"
                    opacity={0.9}
                    style={{ pointerEvents: 'none' }}
                    data-testid="couch-top-line"
                />
            </svg>
        </Box>
    );
}
